import { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import {
  PreparedRecording,
  PreparedRecordingController,
} from "@/store/audio/recording/prepared-recording-controller";
import { DateTime } from "luxon";

export type PrewarmReason =
  | "app-boot"
  | "meeting-detected"
  | "custom-vocabulary-changed"
  | "active-recording-changed"
  | "post-claim"
  | "rewarm-timer";

/**
 * The single owner of prewarmed recording assets.
 *
 * Recording starts feel instant because the expensive parts — Recall's
 * prepareDesktopRecording() and the backend SDK-upload token — are fetched
 * ahead of time. The pool holds exactly one prewarmed slot; once claimed, the
 * assets belong to the recording session and the pool re-warms itself.
 * Prepared assets are window-agnostic (see PreparedRecording), so the slot is
 * valid for any start and never needs to be matched against a meeting.
 *
 * See https://docs.recall.ai/docs/desktop-sdk for the underlying SDK flow.
 */
export class PreparedRecordingPool {
  private slot: PreparedRecording | null = null;
  private slotWarmedAtMs = 0;
  private rewarmTimeout = 0;
  private isDisposed = false;

  /** Proactively replace the slot after this long, well before the 2.5h
   *  expiry of the Recall assets it holds. */
  private static readonly REWARM_INTERVAL_MS = 3600_000; // 1 hour

  constructor(
    private readonly args: {
      controller: PreparedRecordingController;
      getCustomVocabulary: () => string[] | null;
      /** Prewarming is skipped while a recording is actively in progress. */
      isIdle: () => boolean;
    }
  ) {}

  /**
   * Idempotent keep-warm: mints a fresh slot when the current one is missing,
   * expired, or older than the rewarm interval, and (re)schedules the next
   * timer-driven warm attempt (which covers long idle stretches with no other
   * ensureWarm trigger).
   */
  ensureWarm(reason: PrewarmReason): void {
    if (this.isDisposed) return;
    this.scheduleRewarm();

    if (!this.args.isIdle()) return;
    if (this.slot && this.isUsable(this.slot) && !this.isStale()) return;

    this.slot = this.mint({ reason });
    this.slotWarmedAtMs = Date.now();
  }

  /**
   * Hands ownership of prepared assets to a starting recording.
   *
   * Without an id, the slot is consumed (or fresh assets are minted inline on
   * a cold pool). With an id — replays, retries, resumes — the slot is used
   * only if it was minted for that id; otherwise fresh assets are minted for
   * the requested id and the slot is left intact for the next start.
   */
  claim({ audioRecordingId }: { audioRecordingId?: Uuid } = {}): PreparedRecording {
    const slot = this.takeUsableSlot();
    const slotMatches = !!slot && (!audioRecordingId || slot.audioRecordingId === audioRecordingId);

    if (slot && !slotMatches) {
      // An id-bound miss leaves the warm slot for the next anonymous start.
      this.slot = slot;
    }

    const preparedRecording = slotMatches
      ? slot
      : this.mint({ reason: "claim-miss", audioRecordingId });

    logger.info({
      message: "[PreparedRecordingPool] claim",
      info: {
        audioRecordingId: preparedRecording.audioRecordingId,
        requestedAudioRecordingId: audioRecordingId ?? null,
        prewarmed: slotMatches,
      },
    });

    // Re-warm immediately after a claim so the next start is also instant
    // (no-op while a recording is active).
    this.ensureWarm("post-claim");

    return preparedRecording;
  }

  /** Drop the slot so the next ensureWarm mints fresh assets (e.g. when the
   *  custom vocabulary baked into the credentials changes). */
  invalidate(reason: string): void {
    if (!this.slot) return;
    logger.info({
      message: "[PreparedRecordingPool] invalidate",
      info: { reason, audioRecordingId: this.slot.audioRecordingId },
    });
    this.slot = null;
  }

  dispose(): void {
    this.isDisposed = true;
    this.slot = null;
    clearTimeout(this.rewarmTimeout);
  }

  /** Removes and returns the slot if it is still usable; drops it otherwise. */
  private takeUsableSlot(): PreparedRecording | null {
    const slot = this.slot;
    this.slot = null;
    return slot && this.isUsable(slot) ? slot : null;
  }

  private mint({
    reason,
    audioRecordingId,
  }: {
    reason: PrewarmReason | "claim-miss";
    audioRecordingId?: Uuid;
  }): PreparedRecording {
    const preparedRecording = this.args.controller.prepareRecording({
      customVocabulary: this.args.getCustomVocabulary(),
      audioRecordingId: audioRecordingId ?? uuidModule.generate(),
    });

    logger.info({
      message: "[PreparedRecordingPool] minted prepared recording",
      info: { reason, audioRecordingId: preparedRecording.audioRecordingId },
    });

    // A slot whose prewarm failed must not be handed to a future start —
    // drop it so the next ensureWarm/claim mints fresh assets.
    const dropFailedSlot = (error: unknown) => {
      if (this.slot !== preparedRecording) return;
      this.slot = null;
      logger.warn({
        message: "[PreparedRecordingPool] dropped failed prewarm",
        info: {
          audioRecordingId: preparedRecording.audioRecordingId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    };
    void preparedRecording.wholeDesktopWindowId.catch(dropFailedSlot);
    void preparedRecording.recallUploadCredentials.catch(dropFailedSlot);

    return preparedRecording;
  }

  private isUsable(preparedRecording: PreparedRecording): boolean {
    return preparedRecording.expiresAt >= DateTime.local();
  }

  private isStale(): boolean {
    return Date.now() - this.slotWarmedAtMs >= PreparedRecordingPool.REWARM_INTERVAL_MS;
  }

  private scheduleRewarm(): void {
    clearTimeout(this.rewarmTimeout);
    this.rewarmTimeout = window.setTimeout(() => {
      this.ensureWarm("rewarm-timer");
    }, PreparedRecordingPool.REWARM_INTERVAL_MS);
  }
}
